import React from "react";
import classNames from "classnames";

type ButtonVariationProps = {
  primary?: boolean;
  secondary?: boolean;
  success?: boolean;
  warning?: boolean;
  danger?: boolean;
  purple?: boolean;
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonVariationProps {
  noSpacing?: boolean;
  solid?: boolean;
  white?: boolean;
  outline?: boolean;
  transparent?: boolean;
  rounded?: boolean;
  small?: boolean;
  isLoading?: boolean;
  hide?: boolean;
  type?: "button" | "submit" | "reset";
}

const validateVariationProps = ({
  primary,
  secondary,
  success,
  warning,
  danger,
  purple,
}: ButtonVariationProps) => {
  const count =
    Number(!!primary) +
    Number(!!secondary) +
    Number(!!success) +
    Number(!!warning) +
    Number(!!danger) +
    Number(!!purple);

  if (count > 1) {
    console.error(
      "Only one of primary, secondary, success, warning, danger or purple can be true"
    );
  }
};

const Button: React.FC<ButtonProps> = ({
  children,
  noSpacing,
  solid,
  primary,
  secondary,
  success,
  warning,
  danger,
  purple,
  white,
  outline,
  transparent,
  rounded,
  small,
  type = "button",
  isLoading,
  disabled,
  hide = false,
  className,
  ...btnProps
}) => {
  // runtime validation similar to previous PropTypes validator
  validateVariationProps({ primary, secondary, success, warning, danger });

  if (hide) return null;

  const classes = classNames(
    className,
    "btn kg-btn d-flex-inline align-items-center",
    {
      "p-0": noSpacing,
      "py-2 px-3": !noSpacing,
      "kg-primary": primary && !solid,
      "kg-secondary": secondary && !solid,
      "kg-success": success && !solid,
      "kg-warning": warning && !solid,
      "kg-danger": danger && !solid,
      "kg-white": white && !solid,
      "kg-purple": purple && !solid && !outline,
      "kg-bg-primary kg-white": primary && solid,
      "kg-bg-secondary kg-white": secondary && solid,
      "kg-bg-success kg-white": success && solid,
      "kg-bg-warning kg-white": warning && solid,
      "kg-bg-danger kg-white": danger && solid,
      "kg-btn-purple": purple && solid,
      "bg-transparent": transparent,
      "rounded-pill": rounded,
      "kg-primary border kg-border-primary": outline && primary,
      "kg-secondary border kg-border-secondary": outline && secondary,
      "kg-success border kg-border-success": outline && success,
      "kg-warning border kg-border-warning": outline && warning,
      "kg-danger border kg-border-danger": outline && danger,
      "kg-purple border kg-border-purple": outline && purple,
      "border border-white": outline && white && transparent,
      "border kg-border-dark kg-bg-white kg-light-dark": outline && white && !transparent,
      "btn-sm": small,
    }
  );

  return (
    <button
      {...btnProps}
      type={type}
      className={classes}
      disabled={isLoading || disabled}
    >
      {isLoading && (
        <>
          <span
            className="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          ></span>
          <span className="text-capitalize">Loading...</span>
        </>
      )}
      {!isLoading && children}
    </button>
  );
};

export default Button;
